import React from "react";
import { Row, Col, Form, Alert } from "react-bootstrap";
import { useFormikContext } from "formik";

import FormSection from "../FormSection";
import MoneyField from "../MoneyField";

import { useFormat } from "../../../../components/FormatContext";
import { fromCents } from "../../utils/money";

const PricingTab = ({
    insuranceQuotes,
    recalculate,
    needsRecalc,
    isPricingLoading,
    lastError,
    isHydrating,
    markDirtyPricing,
}) => {
    const { setFieldValue, values } = useFormikContext();
    const { formatCurrency } = useFormat();

    const INSURANCE_LABEL_MAP = {
        MEDICAL: "Medical Insurance",
        MEDICAL_PASSENGER: "Passenger Medical Insurance",
        CANCELATION: "Cancellation Insurance",
        INTERNATIONAL_LICENSE: "International Driving License",
    };

    const rentalOrderInsurance = values?.rentalOrderInsurance || [];
    const currency = values?.pricingSnapshot?.calculation?.customerCurrencyCode || "EUR";

    function LockLabel({ text, checked, onChange }) {
        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    width: "100%",
                }}
            >
                <span>{text}</span>
                <Form.Check
                    type="checkbox"
                    label="Lock"
                    checked={!!checked}
                    onChange={(e) => onChange(e.target.checked)}
                />
            </div>
        );
    }

    return (
        <FormSection title="Pricing & Settlement">
            <FormSection title="Rental Settlement" level={2}>
                {lastError && <Alert variant="danger">{String(lastError)}</Alert>}

                <Row>
                    <Col md={6}>
                        <Form.Label className="mb-2">motorcyclePrice:</Form.Label>
                        <div className="d-flex align-items-center mb-3">
                            {formatCurrency(
                                fromCents(values?.pricingSnapshot?.calculation?.motorcycleTotalWithFeesCents || 0),
                                currency
                            )}
                        </div>

                        <Form.Label className="mb-2">extrasPrice:</Form.Label>
                        <div className="d-flex align-items-center mb-3">
                            {formatCurrency(
                                fromCents(values?.pricingSnapshot?.calculation?.extrasTotalWithFeesCents || 0),
                                currency
                            )}
                        </div>

                        <MoneyField
                            name="invoiceSnapshot.calculation.rmbDiscountCents"
                            label={
                                <LockLabel
                                    text="Discount RMB"
                                    checked={values?.pricingSnapshot?.calculation?.discountLocks?.rmbDiscountLocked}
                                    onChange={(checked) =>
                                        setFieldValue(
                                            "pricingSnapshot.calculation.discountLocks.rmbDiscountLocked",
                                            checked
                                        )
                                    }
                                />
                            }
                            currencyCode={currency}
                        />

                        <MoneyField
                            name="invoiceSnapshot.calculation.supplierDiscountCents"
                            label={
                                <LockLabel
                                    text="Discount Supplier"
                                    checked={values?.pricingSnapshot?.calculation?.discountLocks?.supplierDiscountLocked}
                                    onChange={(checked) =>
                                        setFieldValue(
                                            "pricingSnapshot.calculation.discountLocks.supplierDiscountLocked",
                                            checked
                                        )
                                    }
                                />
                            }
                            currencyCode={currency}
                        />
                    </Col>

                    <Col md={6}>
                        <Form.Label>Insurance & Platform Services:</Form.Label>

                        <div className="d-flex align-items-center mb-3">
                            {formatCurrency(
                                fromCents(values?.pricingSnapshot?.calculation?.insurancesTotalCents || 0),
                                currency
                            )}
                        </div>

                        <div className="border-top py-2 mb-3">
                            {(insuranceQuotes || []).map((quote) => {
                                const label = INSURANCE_LABEL_MAP[quote.type] || quote.type || "Insurance";

                                const currentIndex = rentalOrderInsurance.findIndex(
                                    (item) => item.insuranceId === quote.id
                                );
                                const checked = currentIndex !== -1;

                                return (
                                    <Form.Group
                                        className="mb-2 d-flex justify-content-between align-items-center"
                                        key={quote.id}
                                    >
                                        <Form.Check
                                            type="switch"
                                            id={`insurance-${quote.id}`}
                                            label={label}
                                            checked={checked}
                                            onChange={(e) => {
                                                const isChecked = e.target.checked;
                                                const current = rentalOrderInsurance;
                                                markDirtyPricing();

                                                let next = current;

                                                if (isChecked && !checked) {
                                                    const newItem = {
                                                        insuranceId: quote.id,
                                                        type: quote.type,
                                                        name: label,
                                                        priceCentsEuro: quote.priceCentsEuro,
                                                        priceCents: quote.priceCents,
                                                        costCents: quote.costCents,
                                                    };
                                                    next = [...current, newItem];
                                                } else if (!isChecked && checked) {
                                                    next = [
                                                        ...current.slice(0, currentIndex),
                                                        ...current.slice(currentIndex + 1),
                                                    ];
                                                } else {
                                                    return;
                                                }

                                                setFieldValue("rentalOrderInsurance", next);
                                            }}
                                        />

                                        <span>
                                            {formatCurrency(fromCents(quote.priceCents || 0), currency)}
                                        </span>
                                    </Form.Group>
                                );
                            })}
                        </div>
                    </Col>
                </Row>
            </FormSection>

            <FormSection title="Settlement Information" level={2}>
                <Row>
                    <Col md={6}>
                        <Form.Label className="mb-2">Total Rental Price:</Form.Label>
                        <div className="d-flex align-items-center mb-3">
                            {formatCurrency(
                                fromCents(values?.pricingSnapshot?.calculation?.totalPriceCents || 0),
                                currency
                            )}
                        </div>

                        <Form.Label className="mb-2 fw-semibold">Final Price:</Form.Label>
                        <div className="d-flex align-items-center mb-3 fw-semibold">
                            {formatCurrency(
                                fromCents(values?.pricingSnapshot?.calculation?.finalPriceCents || 0),
                                currency
                            )}
                        </div>
                    </Col>

                    <Col md={6}>
                        <MoneyField
                            name="pricingSnapshot.calculation.paidAmountCents"
                            label={`Prepayment (${formatCurrency(
                                fromCents(
                                    (values?.pricingSnapshot?.calculation?.prepaymentAmountCents || 0) +
                                    (values?.pricingSnapshot?.calculation?.insurancesTotalCents || 0)
                                ),
                                currency
                            )})`}
                            currencyCode={currency}
                        />

                        <Form.Label className="mb-2">To pay in destination:</Form.Label>
                        <div className="d-flex align-items-center mb-3">
                            {formatCurrency(
                                fromCents(values?.pricingSnapshot?.calculation?.finalPriceCents || 0) -
                                fromCents(values?.pricingSnapshot?.calculation?.paidAmountCents || 0),
                                currency
                            )}
                        </div>
                    </Col>
                </Row>
            </FormSection>

            <FormSection title="Invoice Information" level={2}>
                <Row>
                    <Col md={6}>
                        <Form.Label className="mb-2">comission:</Form.Label>
                        <div className="d-flex align-items-center mb-3">
                            {formatCurrency(
                                fromCents(values?.invoiceSnapshot?.calculation?.commissionCents || 0),
                                currency
                            )}
                        </div>

                        <Form.Label className="mb-2">managementFee:</Form.Label>
                        <div className="d-flex align-items-center mb-3">
                            {formatCurrency(
                                fromCents(values?.invoiceSnapshot?.calculation?.managementFeeCents || 0),
                                currency
                            )}
                        </div>

                        <Form.Label className="mb-2">Total Profit:</Form.Label>
                        <div className="d-flex align-items-center mb-3">
                            {formatCurrency(
                                fromCents(
                                    (values?.invoiceSnapshot?.calculation?.commissionCents || 0) +
                                    (values?.invoiceSnapshot?.calculation?.managementFeeCents || 0) -
                                    (values?.invoiceSnapshot?.calculation?.rmbDiscountCents || 0)
                                ) - (values?.invoiceSnapshot?.calculation?.insurancesTotalCents || 0),
                                currency
                            )}
                        </div>
                    </Col>

                    <Col md={6}>
                        <Form.Label className="mb-2">To Pay to Supplier:</Form.Label>
                        <div className="d-flex align-items-center mb-3">
                            {formatCurrency(
                                fromCents(values?.pricingSnapshot?.calculation?.paidAmountCents || 0) -
                                fromCents(values?.pricingSnapshot?.calculation?.prepaymentAmountCents || 0) -
                                fromCents(values?.pricingSnapshot?.calculation?.insurancesTotalCents || 0),
                                currency
                            )}
                        </div>
                    </Col>
                </Row>
            </FormSection>
        </FormSection>
    );
};

export default PricingTab;